import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ConfirmDialog, { ConfirmDialogButton } from './ConfirmDialog'
import { ConfirmButtonsConfig, ConfirmDialogContextValue, ConfirmDialogOptions, ConfirmResult } from './ConfirmDialog.types'

interface ConfirmRequest {
    id: number
    options: ConfirmDialogOptions<ConfirmButtonsConfig>
    resolve: (value: string | null) => void
}

export const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null)

const ConfirmDialogProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const [queue, setQueue] = useState<ConfirmRequest[]>([])
    const [activeRequest, setActiveRequest] = useState<ConfirmRequest | null>(null)
    const [loadingKey, setLoadingKey] = useState<string | null>(null)
    const idRef = useRef(0)

    // Promote the next queued request once the current one is settled
    useEffect(() => {
        if (!activeRequest && queue.length > 0) {
            const [next, ...rest] = queue
            setQueue(rest)
            setActiveRequest(next)
        }
    }, [activeRequest, queue])

    const confirm = useCallback(<TButtons extends ConfirmButtonsConfig>(options: ConfirmDialogOptions<TButtons>) => {
        return new Promise<ConfirmResult<TButtons>>((resolve) => {
            const request: ConfirmRequest = {
                id: idRef.current++,
                options: options as ConfirmDialogOptions<ConfirmButtonsConfig>,
                resolve: resolve as (value: string | null) => void,
            }
            setQueue((prev) => [...prev, request])
        })
    }, [])

    const settle = useCallback(
        (value: string | null) => {
            activeRequest?.resolve(value)
            setActiveRequest(null)
            setLoadingKey(null)
        },
        [activeRequest],
    )

    const handleButtonClick = useCallback(
        async (key: string) => {
            if (!activeRequest || loadingKey) return
            const button = activeRequest.options.buttons[key]
            if (!button) return

            if (!button.action) {
                settle(key)
                return
            }

            setLoadingKey(key)
            try {
                await button.action()
                settle(key)
            } catch (error) {
                console.log('Error executing button action: No Action', error)
                setLoadingKey(null)
            }
        },
        [activeRequest, loadingKey, settle],
    )

    const handleClose = useCallback(() => {
        if (loadingKey) return
        settle(null)
    }, [loadingKey, settle])

    const renderedButtons: ConfirmDialogButton[] = activeRequest
        ? Object.entries(activeRequest.options.buttons).map(([key, button]) => ({
              label: button.label,
              variant: button.variant,
              loading: loadingKey === key,
              disabled: button.disabled || (loadingKey !== null && loadingKey !== key),
              onClick: () => handleButtonClick(key),
          }))
        : []

    const contextValue = useMemo(() => ({ confirm }), [confirm])

    return (
        <ConfirmDialogContext.Provider value={contextValue}>
            {children}
            <ConfirmDialog
                open={!!activeRequest}
                type={activeRequest?.options.type}
                title={activeRequest?.options.title ?? ''}
                message={activeRequest?.options.message}
                buttons={renderedButtons}
                onClose={handleClose}
                showCloseButton={activeRequest?.options.showCloseButton ?? true}
                closeOnBackdropClick={activeRequest?.options.closeOnBackdropClick ?? true}
                closeOnEscape={activeRequest?.options.closeOnEscape ?? true}
            />
        </ConfirmDialogContext.Provider>
    )
}

export default ConfirmDialogProvider
